import React, { useMemo } from "react";
import { formatDuration } from "../../utils/formatDuration";
import { formatMoneyByCurrency } from "../../utils/currency";
import { TalentCard } from "./TalentCard";
import { DjDevice, DjStats, DjUser, FeeTier, Order } from "./types";

// The talent card (TalentCard.tsx: Identity / Money / Controls) and the five
// KPI tiles in one row under it.
export function DjSummaryRow({
  dj,
  setDj,
  feeTier,
  devices,
  stats,
  orders,
}: {
  dj: DjUser;
  setDj: React.Dispatch<React.SetStateAction<DjUser | null>>;
  feeTier: FeeTier | null;
  devices: DjDevice[];
  stats: DjStats | null;
  orders: Order[];
}) {
  // Average response time across all orders with respondedAt
  const avgResponseTime = useMemo(() => {
    const responded = orders.filter((o) => o.respondedAt && o.createdAt);
    if (responded.length === 0) return { ms: null, count: 0 };
    const totalMs = responded.reduce((acc, o) => {
      return acc + (new Date(o.respondedAt!).getTime() - new Date(o.createdAt).getTime());
    }, 0);
    return { ms: totalMs / responded.length, count: responded.length };
  }, [orders]);

  const revenueEntries = stats ? Object.entries(stats.revenueByCurrency) : [];

  return (
    <div className="ac-summary adm-mb-16">
      <TalentCard dj={dj} setDj={setDj} feeTier={feeTier} devices={devices} />

      {/* KPI stats */}
      <div className="ac-kpis">
        <div className="kpi-card adm-cursor-default">
          <div className="kpi-icon green-bg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
          </div>
          <div>
            <div className="kpi-label">Lifetime Revenue</div>
            <div className="kpi-value-row">
              {revenueEntries.length > 0
                ? revenueEntries.map(([cur, amt]) => (
                    <span key={cur}>{cur.toUpperCase()} {amt.toFixed(0)}</span>
                  ))
                : <span>$0</span>}
            </div>
          </div>
        </div>

        <div className="kpi-card adm-cursor-default">
          <div className="kpi-icon blue-bg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
          </div>
          <div>
            <div className="kpi-label">Total Orders</div>
            <div className="kpi-value">{stats?.totalOrders ?? 0}</div>
          </div>
        </div>

        <div className="kpi-card adm-cursor-default">
          <div className="kpi-icon orange-bg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </div>
          <div>
            <div className="kpi-label">Revenue (7d)</div>
            <div className="kpi-value">
              {formatMoneyByCurrency(
                stats?.revenue7dByCurrency && Object.keys(stats.revenue7dByCurrency).length > 0
                  ? stats.revenue7dByCurrency
                  : { usd: stats?.revenue7d ?? 0 } // legacy server fallback
              )}
            </div>
          </div>
        </div>

        <div className="kpi-card adm-cursor-default">
          <div className="kpi-icon purple-bg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <div>
            <div className="kpi-label">Orders (7d)</div>
            <div className="kpi-value">{stats?.orders7dCount ?? 0}</div>
          </div>
        </div>

        <div className="kpi-card adm-cursor-default">
          <div className="kpi-icon red-bg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--adm-red)" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div>
            <div className="kpi-label">Avg. Response Time</div>
            <div className="kpi-value">{avgResponseTime.ms != null ? formatDuration(avgResponseTime.ms) : "N/A"}</div>
            {avgResponseTime.count > 0 && (
              <div className="kpi-sublabel">{avgResponseTime.count} responses</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
